// HeroTransition.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/* ─────────────────────────────────────────
   SCROLL REVEAL HOOK
───────────────────────────────────────── */
export const useScrollReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
};

/* ─────────────────────────────────────────
   REVEAL SECTION WRAPPER
───────────────────────────────────────── */
export const RevealSection = ({ children, delay = 0 }) => {
  const { ref, visible } = useScrollReveal();
  return (
    <Box
      ref={ref}
      sx={{
        transition: `opacity 0.8s cubic-bezier(.4,0,.2,1) ${delay}ms,
                     transform 0.8s cubic-bezier(.4,0,.2,1) ${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(52px)',
      }}
    >
      {children}
    </Box>
  );
};

/* ─────────────────────────────────────────
   PARTICLE CANVAS
───────────────────────────────────────── */
const ParticleCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const COLORS = [
      '#f7c59f', '#e8a0bf', '#c9b8f5',
      '#f9e4b7', '#a8d8ea', '#fde68a', '#b5ead4',
    ];

    const particles = Array.from({ length: 90 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 3 + 1,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      alpha: Math.random() * 0.55 + 0.15,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

/* ─────────────────────────────────────────
   FLOATING ORBS
───────────────────────────────────────── */
const FloatingOrbs = () => {
  const orbs = [
    { size: 520, top: '2%',  left: '-8%',  color: '#f7c59f', delay: '0s',   dur: '10s' },
    { size: 420, top: '25%', right: '-6%', color: '#c9b8f5', delay: '3s',   dur: '12s' },
    { size: 360, top: '50%', left: '3%',   color: '#a8d8ea', delay: '1.5s', dur: '9s'  },
    { size: 300, top: '70%', right: '5%',  color: '#e8a0bf', delay: '2.5s', dur: '11s' },
    { size: 260, top: '15%', left: '42%',  color: '#fde68a', delay: '4s',   dur: '8s'  },
    { size: 290, top: '88%', left: '30%',  color: '#b5ead4', delay: '0.8s', dur: '13s' },
    { size: 200, top: '42%', left: '65%',  color: '#f7c59f', delay: '2s',   dur: '10s' },
  ];

  return (
    <>
      {orbs.map((orb, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width:  orb.size,
            height: orb.size,
            borderRadius: '50%',
            background: orb.color,
            opacity: 0.1,
            filter: 'blur(85px)',
            top:   orb.top,
            left:  orb.left,
            right: orb.right,
            animation: `orbFloat ${orb.dur} ease-in-out infinite`,
            animationDelay: orb.delay,
            pointerEvents: 'none',
            '@keyframes orbFloat': {
              '0%, 100%': { transform: 'translateY(0px)   scale(1)'    },
              '50%':      { transform: 'translateY(-40px) scale(1.05)' },
            },
          }}
        />
      ))}
    </>
  );
};

/* ─────────────────────────────────────────
   CONFETTI SQUARES
───────────────────────────────────────── */
const ConfettiSquares = () => {
  const squares = [
    { top: '3%',  left: '7%',  size: 10, color: '#f7c59f', delay: '0s',   dur: '6s'   },
    { top: '7%',  right: '10%',size: 8,  color: '#e8a0bf', delay: '1s',   dur: '8s'   },
    { top: '16%', left: '2%',  size: 12, color: '#c9b8f5', delay: '2s',   dur: '7s'   },
    { top: '22%', right: '4%', size: 7,  color: '#a8d8ea', delay: '0.5s', dur: '9s'   },
    { top: '38%', left: '94%', size: 9,  color: '#fde68a', delay: '1.5s', dur: '6.5s' },
    { top: '48%', left: '1%',  size: 11, color: '#b5ead4', delay: '3s',   dur: '7.5s' },
    { top: '60%', right: '2%', size: 8,  color: '#f7c59f', delay: '0.8s', dur: '8.5s' },
    { top: '70%', left: '5%',  size: 10, color: '#e8a0bf', delay: '2.2s', dur: '6s'   },
    { top: '78%', right: '8%', size: 7,  color: '#c9b8f5', delay: '1.2s', dur: '9.5s' },
    { top: '86%', left: '52%', size: 9,  color: '#fde68a', delay: '0.3s', dur: '7s'   },
    { top: '33%', left: '47%', size: 6,  color: '#a8d8ea', delay: '1.8s', dur: '8s'   },
    { top: '57%', left: '76%', size: 11, color: '#b5ead4', delay: '2.8s', dur: '6.5s' },
  ];

  return (
    <>
      {squares.map((s, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width:  s.size,
            height: s.size,
            background: s.color,
            opacity: 0.5,
            borderRadius: '2px',
            top:   s.top,
            left:  s.left,
            right: s.right,
            pointerEvents: 'none',
            animation: `squareSpin ${s.dur} linear ease-in-out infinite`,
            animationDelay: s.delay,
            '@keyframes squareSpin': {
              '0%':   { transform: 'rotate(0deg)   translateY(0px)'   },
              '50%':  { transform: 'rotate(180deg) translateY(-18px)' },
              '100%': { transform: 'rotate(360deg) translateY(0px)'   },
            },
          }}
        />
      ))}
    </>
  );
};

/* ─────────────────────────────────────────
   DIAGONAL GRID TEXTURE
───────────────────────────────────────── */
const DiagonalGrid = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 60px,
          rgba(106,17,203,0.022) 60px,
          rgba(106,17,203,0.022) 61px
        )
      `,
      pointerEvents: 'none',
    }}
  />
);

/* ─────────────────────────────────────────
   GRADIENT MESH (slow morph)
───────────────────────────────────────── */
const GradientMesh = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      background: `
        radial-gradient(ellipse at 20% 10%,  rgba(247,197,159,0.2)  0%, transparent 50%),
        radial-gradient(ellipse at 80% 15%,  rgba(201,184,245,0.2)  0%, transparent 50%),
        radial-gradient(ellipse at 10% 55%,  rgba(168,216,234,0.18) 0%, transparent 50%),
        radial-gradient(ellipse at 90% 65%,  rgba(232,160,191,0.18) 0%, transparent 50%),
        radial-gradient(ellipse at 50% 95%,  rgba(181,234,212,0.18) 0%, transparent 50%)
      `,
      animation: 'meshMorph 16s ease-in-out infinite alternate',
      '@keyframes meshMorph': {
        '0%':   { opacity: 0.75, transform: 'scale(1)'    },
        '100%': { opacity: 1,    transform: 'scale(1.06)' },
      },
      pointerEvents: 'none',
    }}
  />
);

/* ─────────────────────────────────────────
   PULSE RINGS
───────────────────────────────────────── */
const PulseRings = () => (
  <>
    {[
      { size: 650, top: '5%',  left: '-15%', delay: '0s'   },
      { size: 550, top: '45%', right: '-12%',delay: '3.5s' },
      { size: 450, top: '78%', left: '18%',  delay: '1.8s' },
    ].map((ring, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          width:  ring.size,
          height: ring.size,
          borderRadius: '50%',
          border: '1.5px solid rgba(106,17,203,0.06)',
          top:   ring.top,
          left:  ring.left,
          right: ring.right,
          pointerEvents: 'none',
          animation: 'ringPulse 9s ease-in-out infinite',
          animationDelay: ring.delay,
          '@keyframes ringPulse': {
            '0%, 100%': { transform: 'scale(1)',    opacity: 0.5  },
            '50%':      { transform: 'scale(1.07)', opacity: 0.12 },
          },
        }}
      />
    ))}
  </>
);

/* ─────────────────────────────────────────
   ANIMATED BACKGROUND WRAPPER
   Use this in App.js to wrap <Container>
───────────────────────────────────────── */
export const AnimatedBackground = ({ children }) => (
  <Box
    sx={{
      position: 'relative',
      background: `linear-gradient(160deg,
        #fefaf6 0%,
        #f9f4ff 35%,
        #f0f8ff 65%,
        #fef9f0 100%
      )`,
      overflow: 'hidden',
    }}
  >
    {/* Background decoration layers */}
    <GradientMesh />
    <DiagonalGrid />
    <FloatingOrbs />
    <ConfettiSquares />
    <PulseRings />
    <ParticleCanvas />

    {/* Content above all bg layers */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      {children}
    </Box>
  </Box>
);

/* ─────────────────────────────────────────
   DEFAULT EXPORT — wave SVG after Hero
───────────────────────────────────────── */
const HeroTransition = () => (
  <Box sx={{ lineHeight: 0, overflow: 'hidden', mt: '-2px' }}>
    <svg
      viewBox="0 0 1440 100"
      preserveAspectRatio="none"
      style={{ display: 'block', width: '100%', height: 100 }}
    >
      <defs>
        <linearGradient id="heroWaveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#fefaf6" />
          <stop offset="50%"  stopColor="#f9f4ff" />
          <stop offset="100%" stopColor="#f0f8ff" />
        </linearGradient>
      </defs>
      <path
        d="M0,0 C360,100 1080,0 1440,80 L1440,100 L0,100 Z"
        fill="url(#heroWaveGrad)"
      />
    </svg>
  </Box>
);

export default HeroTransition;